import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ArrowLeft, ShoppingCart, Star, MessageSquare, FileText, Trash2, Lock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const DocumentDetail = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [documentData, setDocumentData] = useState(null);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [fullContent, setFullContent] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [showFeeBreakdown, setShowFeeBreakdown] = useState(false);
  const [hasFile, setHasFile] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [fileLoadAttempted, setFileLoadAttempted] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [pdfHasPreview, setPdfHasPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchDocument();
    fetchRatings();
  }, [id]);

  useEffect(() => {
    // Load file for viewing when document is loaded and user has access — once only
    if (hasFile && !fileUrl && !loadingFile && !fileLoadAttempted && documentData && hasPurchased !== null) {
      loadFileForViewing();
    }
    
    // Cleanup blob URL on unmount
    return () => {
      if (fileUrl) {
        window.URL.revokeObjectURL(fileUrl);
      }
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [hasFile, hasPurchased, documentData, fileUrl, loadingFile, fileLoadAttempted]);

  // Load limited preview for non-purchasers
  useEffect(() => {
    if (hasFile && documentData && !hasPurchased && !previewUrl && !loadingPreview) {
      const isOwner = user && documentData.seller_id === user.id;
      const isPdf = fileType && fileType.includes('pdf');
      if (!isOwner && isPdf) {
        loadPreviewPdf();
      }
    }
  }, [hasFile, hasPurchased, documentData, fileType, previewUrl, loadingPreview]);

  const fetchDocument = async () => {
    try {
      const response = await axios.get(`${API}/documents/${id}`);
      setDocumentData(response.data.document);
      setHasPurchased(response.data.has_purchased);
      setFullContent(response.data.full_content);
      setHasFile(response.data.has_file || false);
      setFileName(response.data.file_name);
      setFileType(response.data.file_type);
      setIsDeleted(response.data.is_deleted || false);
      setPdfHasPreview(response.data.pdf_has_preview || false);
    } catch (error) {
      toast.error('Failed to load document');
      navigate('/');
    }
  };

  const fetchRatings = async () => {
    try {
      const response = await axios.get(`${API}/ratings/document/${id}`);
      setRatings(response.data);
    } catch (error) {
      console.error('Failed to fetch ratings');
    }
  };

  const loadPreviewPdf = async () => {
    setLoadingPreview(true);
    try {
      // Backend may return JSON with S3 URL or a blob
      const response = await axios.get(`${API}/documents/${id}/preview-pdf`, { timeout: 30000 });
      if (response.data?.download_url) {
        setPreviewUrl(response.data.download_url);
      } else {
        // Fallback: re-fetch as blob
        const blobResponse = await axios.get(`${API}/documents/${id}/preview-pdf`, {
          responseType: 'blob',
          timeout: 30000
        });
        const blob = new Blob([blobResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPreviewUrl(url);
      }
    } catch (error) {
      console.error('Failed to load preview PDF:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handlePurchase = async () => {
    console.log('=== PURCHASE FLOW STARTED ===');
    setPurchasing(true);
    
    try {
      // Free document — bypass payment gateway
      if (documentData.price === 0) {
        await axios.post(`${API}/payments/claim-free`, {
          document_id: id,
          origin_url: window.location.origin
        });
        toast.success('Document added to your library!');
        setHasPurchased(true);
        setPurchasing(false);
        return;
      }

      const originUrl = window.location.origin;
      toast.info('Redirecting to payment checkout...');
      
      const response = await axios.post(`${API}/payments/checkout`, {
        document_id: id,
        origin_url: originUrl
      });
      
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed. Please try again.');
      setPurchasing(false);
    }
  };

  const handleSubmitRating = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/ratings`, {
        document_id: id,
        rating: parseInt(rating),
        review
      });
      toast.success('Rating submitted!');
      setReview('');
      fetchRatings();
      fetchDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    }
  };


  const handleDeleteDocument = async () => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/documents/${id}`);
      toast.success('Document deleted successfully!');
      // Redirect to home page after successful deletion
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
      setDeleting(false);
    }
  };


  const [docxHtml, setDocxHtml] = useState(null);

  const loadFileForViewing = async () => {
    if (!hasFile) return;
    
    // Check if user can view (purchased or owner)
    const canView = hasPurchased || (user && documentData && user.id === documentData.seller_id);
    if (!canView) return;
    
    const isDocx = fileType?.includes('wordprocessingml') || fileName?.endsWith('.docx');
    
    setLoadingFile(true);
    try {
      if (isDocx) {
        // For DOCX: fetch as text (backend returns HTML), render inline
        const response = await axios.get(`${API}/documents/${id}/file`, { timeout: 30000 });
        const html = typeof response.data === 'string' ? response.data : '';
        setDocxHtml(html);
      } else {
        // For PDFs/images: backend returns S3 signed URL (no attachment disposition for viewing)
        const response = await axios.get(`${API}/documents/${id}/file`, { timeout: 30000 });
        if (response.data?.download_url) {
          // S3 signed URL — use directly for iframe/viewer (inline, no download trigger)
          setFileUrl(response.data.download_url);
        } else {
          // Blob response (watermarked PDF) — fetch as blob
          const blobResponse = await axios.get(`${API}/documents/${id}/file`, {
            responseType: 'blob',
            timeout: 30000
          });
          const blobType = blobResponse.data.type || blobResponse.headers['content-type'] || fileType || 'application/pdf';
          const blob = new Blob([blobResponse.data], { type: blobType });
          const url = window.URL.createObjectURL(blob);
          setFileUrl(url);
        }
      }
    } catch (error) {
      console.error('Failed to load file:', error);
      toast.error('Failed to load file for viewing');
    } finally {
      setLoadingFile(false);
      setFileLoadAttempted(true);
    }
  };

  const handleDownloadFile = async () => {
    setDownloadingFile(true);
    try {
      // Request S3 URL with download=true for Content-Disposition: attachment
      const response = await axios.get(`${API}/documents/${id}/file?download=true`, { timeout: 60000 });
      
      if (response.data?.download_url) {
        // Open S3 signed URL directly — browser handles the download via Content-Disposition
        window.open(response.data.download_url, '_blank');
        toast.success('File downloaded!');
        return;
      }
      
      // Blob response (e.g., watermarked PDFs) — re-fetch as blob
      const blobResponse = await axios.get(`${API}/documents/${id}/file?download=true`, {
        responseType: 'blob',
        timeout: 60000
      });
      
      // Check if response is an error (JSON error returned as blob)
      if (blobResponse.data.type === 'application/json') {
        const text = await blobResponse.data.text();
        const err = JSON.parse(text);
        toast.error(err.detail || 'Download failed');
        return;
      }

      const downloadBlob = new Blob([blobResponse.data], { type: 'application/octet-stream' });
      const downloadName = fileName || 'document.pdf';
      const url = window.URL.createObjectURL(downloadBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        if (link.parentNode) document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 10000);
      
      toast.success('File downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download file. Please try again.');
    } finally {
      setDownloadingFile(false);
    }
  };

  if (!documentData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isOwner = user && user.id === documentData.seller_id;
  const canViewFull = hasPurchased || isOwner;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-blue-50 to-stone-100 relative">
      {/* Loading Overlay */}
      {purchasing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl text-center max-w-md">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Processing Payment</h3>
            <p className="text-gray-600">Redirecting to secure checkout...</p>
          </div>
        </div>
      )}
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 rounded-xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Button>

        {isDeleted && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 text-amber-800">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">This document has been removed from the marketplace by its creator.</span>
            </div>
            {hasPurchased && (
              <p className="text-sm text-amber-700 mt-2 ml-7">
                You can still access the full content because you purchased it before it was removed.
              </p>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 border border-gray-200 mb-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                  {documentData.document_type}
                </span>
                <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                  {documentData.field}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 break-words" style={{ fontFamily: 'Crimson Text, serif' }}>
                {documentData.title}
              </h1>
              <p className="text-gray-600 text-base sm:text-lg mb-6">{documentData.description}</p>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-6">
                <Link to={`/profile/${documentData.seller_id}`} className="flex items-center gap-2 hover:opacity-80">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-blue-100 text-blue-800">
                      {documentData.seller_name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{documentData.seller_name}</p>
                    <p className="text-xs text-gray-500">Seller</p>
                  </div>
                </Link>
                {documentData.average_rating > 0 && (
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                    <span className="text-base sm:text-lg font-semibold text-gray-800">{documentData.average_rating.toFixed(1)}</span>
                    <span className="text-xs sm:text-sm text-gray-500">({ratings.length} reviews)</span>
                  </div>
                )}
                <div className="text-xs sm:text-sm text-gray-500">
                  {documentData.purchase_count} purchases
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {documentData.tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-lg">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="md:ml-8 text-left md:text-right flex-shrink-0">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                {documentData.price === 0 ? 'Free' : `$${documentData.price.toFixed(2)}`}
              </div>
              {!canViewFull && !isOwner && (
                <>
                  {documentData.price > 0 && (
                  <div className="mb-4 text-sm text-gray-600">
                    <button
                      onClick={() => setShowFeeBreakdown(!showFeeBreakdown)}
                      className="hover:text-blue-600 underline"
                    >
                      {showFeeBreakdown ? 'Hide' : 'View'} price breakdown
                    </button>
                    {showFeeBreakdown && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg text-left border border-gray-200">
                        <div className="flex justify-between mb-1">
                          <span>Document price:</span>
                          <span className="font-medium">${documentData.price.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between mb-1 text-gray-500">
                          <span>Platform fee (15%):</span>
                          <span>${(documentData.price * 0.15).toFixed(2)}</span>
                        </div>
                        <div className="border-t border-gray-300 mt-2 pt-2 flex justify-between font-semibold">
                          <span>Total:</span>
                          <span>${(documentData.price * 1.15).toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                  <Button
                    data-testid="purchase-btn"
                    onClick={handlePurchase}
                    disabled={purchasing}
                    className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 w-full sm:w-48 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {purchasing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        {documentData.price === 0 ? 'Claiming...' : 'Redirecting...'}
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        {documentData.price === 0 ? 'Download Free' : 'Purchase'}
                      </>
                    )}
                  </Button>
                </>
              )}
              {hasPurchased && (
                <div className="px-4 py-2 bg-green-100 text-green-800 rounded-xl text-sm font-medium inline-block">
                  Purchased
                </div>
              )}
              {isOwner && (
                <div className="flex flex-wrap gap-2">
                  <div className="px-4 py-2 bg-blue-100 text-blue-800 rounded-xl text-sm font-medium">
                    Your Document
                  </div>
                  <Button
                    onClick={handleDeleteDocument}
                    disabled={deleting}
                    variant="outline"
                    className="px-4 py-2 border-red-300 text-red-600 hover:bg-red-50 rounded-xl text-sm font-medium disabled:opacity-50"
                  >
                    {deleting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
              Preview
            </h2>
            <div className="bg-blue-50/50 rounded-xl p-4 sm:p-6 border border-blue-200">
              <p className="text-gray-700 whitespace-pre-wrap break-words">{documentData.preview_content}</p>
              {!canViewFull && (
                <div className="mt-6 text-center">
                  <p className="text-blue-800 font-medium mb-2">Purchase to view full content</p>
                  <div className="h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent"></div>
                </div>
              )}
            </div>

            {/* Limited PDF Preview for non-purchasers */}
            {!canViewFull && hasFile && fileType?.includes('pdf') && (
              <div data-testid="pdf-preview-limited" className="mt-6 relative">
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 p-3 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Document Preview (Limited)</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Lock className="w-3 h-3" />
                      <span>Content protected</span>
                    </div>
                  </div>
                  <div className="relative" style={{ height: '500px', overflow: 'hidden', userSelect: 'none', WebkitUserSelect: 'none' }}>
                    {loadingPreview ? (
                      <div className="flex items-center justify-center h-full bg-gray-50">
                        <div className="text-center">
                          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                          <p className="text-gray-500 text-sm">Loading preview...</p>
                        </div>
                      </div>
                    ) : previewUrl ? (
                      <>
                        <iframe
                          src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                          className="w-full h-full pointer-events-none"
                          title="Document Preview"
                          style={{ userSelect: 'none' }}
                        />
                        {/* Gradient overlay at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-center pointer-events-auto">
                          <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl shadow-lg hover:bg-blue-700 transition-colors cursor-pointer" onClick={handlePurchase}>
                            <Lock className="w-4 h-4" />
                            <span className="font-medium">{documentData.price === 0 ? 'Download free to unlock' : 'Purchase to unlock full content'}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full bg-gray-50">
                        <div className="text-center p-6">
                          <Lock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500 text-sm">Preview available after purchase</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {canViewFull && (
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
                Full Content
              </h2>
              
              {hasFile ? (
                <div data-testid="full-content" className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {/* Image Viewer */}
                  {fileType?.startsWith('image/') ? (
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-3 sm:p-4 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            <span className="font-medium text-gray-800 truncate">{fileName}</span>
                          </div>
                          <button
                            onClick={handleDownloadFile}
                            disabled={downloadingFile}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                          >
                            {downloadingFile ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Downloading...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download Image
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="p-6 flex justify-center bg-gray-50">
                        {loadingFile ? (
                          <div className="text-center py-12">
                            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading image...</p>
                          </div>
                        ) : fileUrl ? (
                          <img 
                            src={fileUrl} 
                            alt={fileName}
                            className="max-w-full h-auto rounded-lg shadow-lg"
                            style={{ maxHeight: '600px' }}
                          />
                        ) : (
                          <p className="text-gray-500 py-12">Unable to load image</p>
                        )}
                      </div>
                    </div>
                  ) : fileType?.startsWith('video/') ? (
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-3 sm:p-4 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            <span className="font-medium text-gray-800 truncate">{fileName}</span>
                          </div>
                          <button
                            onClick={handleDownloadFile}
                            disabled={downloadingFile}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 w-full sm:w-auto"
                          >
                            {downloadingFile ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Downloading...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download Video
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="p-4 sm:p-6 bg-gray-50">
                        {loadingFile ? (
                          <div className="text-center py-12">
                            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading video...</p>
                          </div>
                        ) : fileUrl ? (
                          <video 
                            controls 
                            className="w-full rounded-lg shadow-lg"
                            style={{ maxHeight: '600px' }}
                          >
                            <source src={fileUrl} type={fileType} />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <p className="text-gray-500 py-12 text-center">Unable to load video</p>
                        )}
                      </div>
                    </div>
                  ) : fileType?.startsWith('audio/') ? (
                    <div className="p-4 sm:p-6">
                      <div className="bg-blue-50 p-4 sm:p-6 rounded-lg border border-blue-200">
                        <FileText className="w-12 sm:w-16 h-12 sm:h-16 text-blue-600 mx-auto mb-4" />
                        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 text-center truncate">{fileName}</h3>
                        {loadingFile ? (
                          <div className="text-center py-6">
                            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading audio...</p>
                          </div>
                        ) : fileUrl ? (
                          <audio controls className="w-full mb-4">
                            <source src={fileUrl} type={fileType} />
                            Your browser does not support the audio tag.
                          </audio>
                        ) : (
                          <p className="text-gray-500 text-center mb-4">Unable to load audio</p>
                        )}
                        <button
                          onClick={handleDownloadFile}
                          disabled={downloadingFile}
                          className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {downloadingFile ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Downloading...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download Audio
                            </>
                          )}
                        </button>
                      </div>
                      {fullContent && (
                        <div className="mt-6">
                          <h3 className="text-lg font-semibold text-gray-800 mb-3">Additional Notes:</h3>
                          <p className="text-gray-700 whitespace-pre-wrap">{fullContent}</p>
                        </div>
                      )}
                    </div>
                  ) : fileType?.includes('pdf') || fileType?.includes('wordprocessingml') || fileName?.endsWith('.docx') ? (
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-3 sm:p-4 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            <span className="font-medium text-gray-800 truncate">{fileName}</span>
                          </div>
                          <button
                            onClick={handleDownloadFile}
                            disabled={downloadingFile}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                          >
                            {downloadingFile ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Downloading...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download {fileName?.endsWith('.docx') ? 'Document' : 'PDF'}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="w-full" style={{ minHeight: '400px' }}>
                        {loadingFile ? (
                          <div className="flex items-center justify-center h-[400px] bg-gray-50">
                            <div className="text-center">
                              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                              <p className="text-gray-600">Loading document...</p>
                            </div>
                          </div>
                        ) : docxHtml ? (
                          <div
                            className="prose max-w-none p-6 bg-white"
                            dangerouslySetInnerHTML={{ __html: docxHtml }}
                          />
                        ) : fileUrl ? (
                          <iframe
                            src={`${fileUrl}#toolbar=0`}
                            className="w-full h-[600px] sm:h-[800px]"
                            title="Document Viewer"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-[400px] bg-gray-50">
                            <p className="text-gray-500">Unable to load document preview</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 sm:p-6">
                      <div className="bg-blue-50 p-4 sm:p-6 rounded-lg border border-blue-200 text-center">
                        <FileText className="w-12 sm:w-16 h-12 sm:h-16 text-blue-600 mx-auto mb-4" />
                        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 truncate">{fileName}</h3>
                        <p className="text-gray-600 mb-4 text-sm sm:text-base">This document is available for download</p>
                        <button
                          onClick={handleDownloadFile}
                          disabled={downloadingFile}
                          className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        >
                          {downloadingFile ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Downloading...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download Document
                            </>
                          )}
                        </button>
                      </div>
                      {fullContent && (
                        <div className="mt-6">
                          <h3 className="text-lg font-semibold text-gray-800 mb-3">Additional Notes:</h3>
                          <p className="text-gray-700 whitespace-pre-wrap">{fullContent}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : fullContent ? (
                <div data-testid="full-content" className="bg-white rounded-xl p-6 border border-gray-200">
                  <p className="text-gray-700 whitespace-pre-wrap">{fullContent}</p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-center text-gray-500">
                  <p>No content available</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Ratings Section */}
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 border border-gray-200">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
            Reviews & Ratings
          </h2>

          {hasPurchased && (
            <form onSubmit={handleSubmitRating} className="mb-8 p-4 sm:p-6 bg-blue-50/50 rounded-xl border border-blue-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Leave a Review</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                <Select value={rating.toString()} onValueChange={(v) => setRating(parseInt(v))}>
                  <SelectTrigger data-testid="rating-select" className="w-32 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map(r => (
                      <SelectItem key={r} value={r.toString()}>
                        {r} {r === 1 ? 'Star' : 'Stars'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Review</label>
                <Textarea
                  data-testid="review-input"
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  required
                  className="rounded-xl"
                  rows={4}
                  placeholder="Share your thoughts about this document..."
                />
              </div>
              <Button
                data-testid="submit-rating-btn"
                type="submit"
                className="rounded-xl bg-blue-600 hover:bg-blue-700"
              >
                Submit Review
              </Button>
            </form>
          )}

          <div className="space-y-4">
            {ratings.map((r) => (
              <div key={r.id} className="p-4 sm:p-6 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10 flex-shrink-0">
                      <AvatarFallback className="bg-blue-100 text-blue-800">
                        {r.buyer_name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-gray-800">{r.buyer_name}</p>
                      <div className="flex items-center gap-1">
                        {[...Array(r.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 ml-13 sm:ml-0">
                    {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <p className="text-gray-700 break-words">{r.review}</p>
              </div>
            ))}
          </div>

          {ratings.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p>No reviews yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentDetail;
